import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, ChevronLeft, ChevronRight, CircleDot, CheckCircle, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const Estados = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'estados');

  // Sorting
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const sortedItems = [...items].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/config/status?page=${pagination.page}&limit=${pagination.limit}`);
      setItems(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) { toast.error('Error al cargar estados'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [pagination.page]);

  const handleCreate = () => { setEditingItem(null); setFormData({ name: '' }); setModalOpen(true); };
  const handleEdit = (item) => { setEditingItem(item); setFormData({ name: item.name }); setModalOpen(true); };
  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingItem) { await api.put(`/config/status/${editingItem.id}`, formData); toast.success('Estado actualizado'); }
      else { await api.post('/config/status', formData); toast.success('Estado creado'); }
      setModalOpen(false); fetchData();
    } catch (error) { toast.error('Error al guardar'); }
  };
  const handleDelete = async (id) => { try { await api.delete(`/config/status/${id}`); toast.success('Estado eliminado'); setDeleteConfirm(null); fetchData(); } catch (error) { toast.error('Error al eliminar'); } };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h1 className="page-title">Estados</h1><p className="page-subtitle">Configuración de estados de activos</p></div>
          {canCreate && <Button className="btn btn-primary" onClick={handleCreate}><Plus className="h-4 w-4" /> Nuevo Estado</Button>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="stats-card">
            <div className="mb-2"><CircleDot className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Estados</div>
          </div>
          <div className="stats-card">
            <div className="mb-2"><CheckCircle className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{items.filter(i => i.name?.toLowerCase().includes('activo')).length}</div>
            <div className="stats-card-label">Activos</div>
          </div>
        </div>

        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (<div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div></div>) : (
              <>
                <table className="table-subtle">
                  <thead><tr>
                    <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('name')}><div className="flex items-center gap-1.5">Nombre <SortIcon field="name" /></div></th>
                    <th>Tipo</th>
                    <th className="text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {sortedItems.length === 0 ? (<tr><td colSpan={3}><div className="empty-state"><CircleDot className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="empty-state-text">No hay estados</p></div></td></tr>) : sortedItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className={`status-dot ${item.name?.toLowerCase().includes('activo') ? 'status-online' : item.name?.toLowerCase().includes('inactivo') ? 'status-offline' : ''}`} />
                            <span className="font-medium">{item.name}</span>
                          </div>
                        </td>
                        <td><span className={`badge ${item.name?.toLowerCase().includes('activo') ? 'badge-success' : item.name?.toLowerCase().includes('inactivo') ? 'badge-error' : 'badge-gray'}`}>{item.name?.toLowerCase().includes('activo') ? 'Operativo' : item.name?.toLowerCase().includes('inactivo') ? 'No operativo' : 'Otro'}</span></td>
                        <td className="text-right"><div className="flex justify-end gap-1">{canUpdate && <button onClick={() => handleEdit(item)} className="action-btn action-btn-edit"><Edit className="h-3.5 w-3.5" /></button>}{canDelete && <button onClick={() => setDeleteConfirm(item.id)} className="action-btn-delete"><Trash2 className="h-3.5 w-3.5" /></button>}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30"><p className="text-sm text-muted-foreground">{((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}</p><div className="flex items-center gap-1"><Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button><span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span><Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8"><ChevronRight className="h-4 w-4" /></Button></div></div>
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <CircleDot className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Estado' : 'Nuevo Estado'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body space-y-4">
              <div><Label className="text-sm font-medium">Nombre *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-subtle mt-1.5" /></div>
            </div>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button><Button className="btn btn-primary" onClick={handleSave}>Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="modal-body text-muted-foreground">¿Está seguro?</p>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button><Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Estados;
